import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  UsersIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  BriefcaseIcon,
  ChartBarIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import apiClient from '../api/config';
import { useToastContext } from '../components/common/Toast';
import { handleApiError } from '../utils/errorHandler';
import PhoneInput from '../components/common/PhoneInput';

interface Employee {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  role: string;
  specialization?: string;
  is_active: boolean;
  created_at: string;
  garage_id: number;
  // Stats
  current_jobs_count?: number;
  completed_jobs_count?: number;
  average_rating?: number;
  performance_score?: number;
}

const EmployeesPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { showSuccess, showError } = useToastContext();
  
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    country_code: '965', // Default to Kuwait
    password: '',
    specialization: '',
    role: 'employee'
  });

  useEffect(() => {
    fetchEmployees();
  }, [currentPage, searchTerm]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/employees', {
        params: {
          page: currentPage,
          page_size: 20,
          search: searchTerm,
          include_stats: true
        }
      });
      
      // Handle response based on backend format
      if (response.data.data) {
        setEmployees(response.data.data);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.pages || 1);
        }
      } else if (response.data.employees) {
        setEmployees(response.data.employees);
        if (response.data.total) {
          setTotalPages(Math.ceil(response.data.total / 20));
        }
      } else {
        setEmployees([]);
      }
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.phone_number || !formData.password) {
      showError('Please fill in all required fields');
      return;
    }

    try {
      const employeeData = {
        ...formData,
        garage_id: user?.garage_id
      };
      
      await apiClient.post('/employees', employeeData);
      showSuccess(t('employeeAddedSuccessfully'));
      setShowAddModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const handleUpdateEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone_number: formData.phone_number,
        specialization: formData.specialization
      };
      
      await apiClient.put(`/employees/${selectedEmployee.id}`, updateData);
      showSuccess(t('employeeUpdatedSuccessfully'));
      setShowEditModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const handleToggleEmployeeStatus = async (employee: Employee) => {
    try {
      if (employee.is_active) {
        await apiClient.post(`/employees/${employee.id}/deactivate`);
        showSuccess(t('employeeDeactivated'));
      } else {
        await apiClient.post(`/employees/${employee.id}/activate`);
        showSuccess(t('employeeActivated'));
      }
      fetchEmployees();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const handleDeleteEmployee = async (id: number) => {
    if (!window.confirm(t('confirmDeleteEmployee'))) return;
    
    try {
      await apiClient.delete(`/employees/${id}`);
      showSuccess(t('employeeDeletedSuccessfully'));
      fetchEmployees();
    } catch (error) {
      const processedError = handleApiError(error);
      showError(processedError.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone_number: '',
      country_code: '965',
      password: '',
      specialization: '',
      role: 'employee'
    });
    setSelectedEmployee(null);
  };

  const openEditModal = (employee: Employee) => {
    setSelectedEmployee(employee);
    setFormData({
      name: employee.name,
      email: employee.email,
      phone_number: employee.phone_number,
      country_code: '965', // Default to Kuwait
      password: '',
      specialization: employee.specialization || '',
      role: employee.role
    });
    setShowEditModal(true);
  };

  return (
    <div className={`container mx-auto px-4 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <UsersIcon className="h-8 w-8 mr-2 text-teal-600" />
            {t('employeeManagement')}
          </h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('addEmployee')}
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchEmployees')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Employees Grid */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center py-8">
            <UsersIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('noEmployeesFound')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {employees.map((employee) => (
              <div key={employee.id} className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-teal-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-semibold text-gray-900">{employee.name}</h3>
                      {employee.specialization && (
                        <p className="text-sm text-gray-600">{employee.specialization}</p>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    employee.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {employee.is_active ? t('active') : t('inactive')}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-600">
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    {employee.email}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    {employee.phone_number}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <BriefcaseIcon className="h-4 w-4 mr-2" />
                    {employee.current_jobs_count || 0} active jobs
                  </div>
                  {employee.average_rating && (
                    <div className="flex items-center text-gray-600">
                      <StarIcon className="h-4 w-4 mr-2 text-yellow-500" />
                      {employee.average_rating.toFixed(1)} / 5.0
                    </div>
                  )}
                </div>

                {/* Stats */}
                {(employee.completed_jobs_count !== undefined || employee.performance_score !== undefined) && (
                  <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-2 text-center">
                    {employee.completed_jobs_count !== undefined && (
                      <div>
                        <p className="text-2xl font-bold text-teal-600">{employee.completed_jobs_count}</p>
                        <p className="text-xs text-gray-600">{t('completedJobs')}</p>
                      </div>
                    )}
                    {employee.performance_score !== undefined && (
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{employee.performance_score}%</p>
                        <p className="text-xs text-gray-600">{t('performance')}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    onClick={() => openEditModal(employee)}
                    className="text-teal-600 hover:text-teal-700 p-2"
                    title={t('edit')}
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleToggleEmployeeStatus(employee)}
                    className={`p-2 ${
                      employee.is_active 
                        ? 'text-orange-600 hover:text-orange-700' 
                        : 'text-green-600 hover:text-green-700'
                    }`}
                    title={employee.is_active ? t('deactivate') : t('activate')}
                  >
                    {employee.is_active ? (
                      <XCircleIcon className="h-5 w-5" />
                    ) : (
                      <CheckCircleIcon className="h-5 w-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeleteEmployee(employee.id)}
                    className="text-red-600 hover:text-red-700 p-2"
                    title={t('delete')}
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {t('previous')}
              </button>
              <span className="px-4 py-1">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {t('next')}
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Add/Edit Employee Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {showEditModal ? t('editEmployee') : t('addEmployee')}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={showEditModal ? handleUpdateEmployee : handleAddEmployee}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('email')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <PhoneInput
                  label={t('phoneNumber')}
                  countryCode={formData.country_code}
                  phoneNumber={formData.phone_number}
                  onCountryCodeChange={(code) => setFormData({ ...formData, country_code: code })}
                  onPhoneNumberChange={(number) => setFormData({ ...formData, phone_number: number })}
                  required
                />

                {showAddModal && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('password')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required={showAddModal}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('specialization')}
                  </label>
                  <select
                    value={formData.specialization}
                    onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">{t('selectSpecialization')}</option>
                    <option value="Engine Specialist">Engine Specialist</option>
                    <option value="Transmission Expert">Transmission Expert</option>
                    <option value="Electrical Systems">Electrical Systems</option>
                    <option value="AC Technician">AC Technician</option>
                    <option value="Body Work">Body Work</option>
                    <option value="Paint Specialist">Paint Specialist</option>
                    <option value="General Mechanic">General Mechanic</option>
                    <option value="Diagnostic Expert">Diagnostic Expert</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  {showEditModal ? t('updateEmployee') : t('addEmployee')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;